import { FC } from 'react';
import Markdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { match } from 'ts-pattern';

import {
  Caption,
  type CaptionProps,
  Heading,
  type HeadingProps,
  Text,
  type TextProps,
} from '@eluve/components';
import { preserveNewlinesForMarkdown } from '@eluve/smart-blocks';

// SDUI sends sizes using the old naming convention. These mappers translate
// them to the current component prop values so the server contract stays stable.

function mapHeadingSize(size?: string): NonNullable<HeadingProps['size']> {
  const map: Record<string, NonNullable<HeadingProps['size']>> = {
    '3xl': '5xl',
    '2xl': '4xl',
    xl: '3xl',
    lg: '2xl',
    md: 'xl',
    sm: 'lg',
  };
  return (size ? map[size] : undefined) ?? '5xl';
}

function mapTextSize(size?: string): NonNullable<TextProps['size']> {
  const map: Record<string, NonNullable<TextProps['size']>> = {
    lg: 'md',
    md: 'sm',
    sm: 'xs',
  };
  return (size ? map[size] : undefined) ?? 'md';
}

function mapCaptionSize(size?: string): NonNullable<CaptionProps['size']> {
  const map: Record<string, NonNullable<CaptionProps['size']>> = {
    md: '2xs',
    sm: '3xs',
  };
  return (size ? map[size] : undefined) ?? '2xs';
}

function createMarkdownComponents(
  size?: TextProps['size'],
  color?: TextColor,
): Components {
  const resolvedSize = size ?? 'xs';
  const resolvedColor = color ?? 'secondary';

  return {
    ul: ({ children }) => (
      <ul className="ml-4 list-outside list-disc space-y-1">{children}</ul>
    ),
    ol: ({ children, start }) => (
      <ol start={start} className="ml-4 list-outside list-decimal space-y-1">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="pl-1">
        <Text
          inline
          size={resolvedSize}
          color={resolvedColor}
          className="leading-relaxed"
        >
          {children}
        </Text>
      </li>
    ),
    strong: ({ children }) => (
      <Text inline weight="bold" color={resolvedColor} size={resolvedSize}>
        {children}
      </Text>
    ),
    em: ({ children }) => (
      <Text inline color={resolvedColor} size={resolvedSize} className="italic">
        {children}
      </Text>
    ),
    p: ({ children }) => (
      <Text
        size={resolvedSize}
        color={resolvedColor}
        className="whitespace-pre-line"
      >
        {children}
      </Text>
    ),
  };
}

type TextColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'supporting'
  | 'disabled'
  | 'inherit';

export interface TextRendererProps {
  content: string;
  variant?: 'heading' | 'subheading' | 'body' | 'label' | 'caption';
  markdown?: boolean;
  size?: '3xl' | '2xl' | 'xl' | 'lg' | 'md' | 'sm';
  weight?: 'bold' | 'semibold' | 'medium' | 'regular';
  color?: TextColor;
}

export const TextRenderer: FC<TextRendererProps> = ({
  content,
  variant = 'body',
  markdown = false,
  size,
  weight,
  color,
}) => {
  if (markdown) {
    return (
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={createMarkdownComponents(
          size ? mapTextSize(size) : undefined,
          color,
        )}
      >
        {preserveNewlinesForMarkdown(content)}
      </Markdown>
    );
  }

  // When explicit size/weight/color are provided, use them directly
  const hasOverrides = size || weight || color;

  if (hasOverrides && (variant === 'heading' || variant === 'subheading')) {
    return (
      <Heading
        size={mapHeadingSize(size)}
        weight={weight as HeadingProps['weight']}
        color={color}
      >
        {content}
      </Heading>
    );
  }

  if (hasOverrides && variant === 'caption') {
    return (
      <Caption
        size={mapCaptionSize(size)}
        weight={weight as CaptionProps['weight']}
        color={color}
      >
        {content}
      </Caption>
    );
  }

  if (hasOverrides) {
    return (
      <Text
        size={mapTextSize(size)}
        weight={weight as TextProps['weight']}
        color={color}
      >
        {content}
      </Text>
    );
  }

  return match(variant)
    .with('heading', () => <Heading size="lg">{content}</Heading>)
    .with('subheading', () => <Text weight="semibold">{content}</Text>)
    .with('body', () => <Text size="xs">{content}</Text>)
    .with('label', () => (
      <Text size="xs" weight="bold" color="supporting">
        {content}
      </Text>
    ))
    .with('caption', () => (
      <Caption size="3xs" color="tertiary">
        {content}
      </Caption>
    ))
    .otherwise(() => <Text size="xs">{content}</Text>);
};
